/**
 * generatePreProcessJsonMetadata_Hierarchy.ts - Hierarchy building for metadata generation
 * Port of C# GeneratePreProcessJsonMetadata_Hierarchy.cs
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { SlotContentInfo, SlotInfo, TemplateInfo } from '../../model/appSiteMetadata.js';
import type { TargetFile } from '../../model/targetFile.js';
import { getTemplate } from './generatePreProcessJsonMetadata_Template.js';

/**
 * Builds hierarchical component structure for a specific AppSite root template only
 * Returns the root template with all components populated recursively
 */
export function buildAppsiteHierarchy(
    allTemplates: Map<string, Map<string, TemplateInfo>>,
    appSiteName: string,
    rootTemplateName: string,
    sourceJsonCache: Map<string, Map<string, TargetFile>> | null = null,
    appView: string = '',
    appViewPrefix: string = 'Main',
    maxRecursionDepth: number = 7
): TemplateInfo {
    // Use getTemplate to retrieve root template with AppView-aware resolution and SearchAppSites fallback
    // This enables root component reuse across AppSites
    const rootResult = getTemplate(allTemplates, appSiteName, rootTemplateName, '', appView, appViewPrefix);
    const rootTemplate = rootResult.template;

    if (!rootTemplate) {
        Logger.warn(`Root template not found: ${appSiteName}/${rootTemplateName}`, 'GeneratePreProcessJsonMetadata');
        const fallback = new TemplateInfo();
        fallback.templateName = rootTemplateName;
        fallback.components = [];
        return fallback;
    }

    // Build hierarchy recursively starting from root using copyTemplateWithHierarchy
    // Pass rootTemplateName as appFileName for DynamicView content type detection
    const rootCopy = copyTemplateWithHierarchy(rootTemplate, allTemplates, appSiteName, sourceJsonCache, appView, appViewPrefix, rootTemplateName, undefined, 0, maxRecursionDepth);

    // Set templateName for root template (should be treated same as all other templates)
    rootCopy.templateName = rootTemplate.templateName; // Use the actual template name from getTemplate

    return rootCopy;
}

/**
 * Creates a deep copy of a template with its full component hierarchy
 */
function copyTemplateWithHierarchy(
    source: TemplateInfo,
    allTemplates: Map<string, Map<string, TemplateInfo>>,
    appSiteName: string,
    sourceJsonCache: Map<string, Map<string, TargetFile>> | null = null,
    appView: string = '',
    appViewPrefix: string = 'Main',
    appFileName: string = '',
    ancestorPath: Set<string> | undefined = undefined,
    depth: number = 0,
    maxRecursionDepth: number = 7
): TemplateInfo {
    const copy = new TemplateInfo();
    copy.htmlFileName = source.htmlFileName;
    copy.jsonFileName = source.jsonFileName;
    copy.placeholderName = source.placeholderName;
    copy.templateName = source.templateName;
    copy.templateAppSite = source.templateAppSite;
    copy.appFileName = appFileName; // Root AppFile name for DynamicView content type detection
    copy.templateType = source.templateType;
    copy.placeholders = [...(source.placeholders || [])];
    copy.nonPlaceholders = [...(source.nonPlaceholders || [])];
    copy.slots = [...(source.slots || [])];
    copy.jsonKeys = [...(source.jsonKeys || [])];
    copy.jsonInheritKeys = new Map(source.jsonInheritKeys || new Map());
    copy.jsonArrayKeys = [...(source.jsonArrayKeys || [])];
    copy.nonJsonKeys = [...(source.nonJsonKeys || [])];
    copy.nonJsonArrayKeys = [...(source.nonJsonArrayKeys || [])];
    copy.conditionalKeys = [...(source.conditionalKeys || [])];
    copy.appViewPlaceholders = new Map(source.appViewPlaceholders);
    copy.slotholders = [];
    copy.nonSlotholders = [];
    copy.components = [];

    ancestorPath ??= new Set<string>();
    const selfPathKey = `${source.templateAppSite ?? ''}/${source.templateName ?? ''}`;
    if (depth > maxRecursionDepth) {
        Logger.warn(`Max hierarchy depth (${maxRecursionDepth}) exceeded at ${selfPathKey} - possible circular template reference; stopping descent.`, 'GeneratePreProcessJsonMetadata');
        return copy;
    }
    const selfPathLower = selfPathKey.toLowerCase();
    const addedSelf = !!source.templateName && !ancestorPath.has(selfPathLower);
    if (addedSelf) {
        ancestorPath.add(selfPathLower);
    }

    // Deep copy slotholders
    for (const slot of (source.slotholders || [])) {
        copy.slotholders.push(copySlotInfo(slot));
    }

    // Deep copy non-slotholders
    for (const slot of (source.nonSlotholders || [])) {
        copy.nonSlotholders.push(copySlotInfo(slot));
    }

    // Recursively build component hierarchy for this copy's placeholders
    for (const placeholderName of copy.placeholders) {
        // For reuse components, check if JSON exists before calling getTemplate
        // Component reuse pattern: CurrentTemplateName_Number_PlaceholderName
        let isReuseComponent = false;
        const parts = placeholderName.split('_');
        if (parts.length >= 3 && source.templateName) {
            for (let i = 1; i < parts.length - 1; i++) {
                if (!isNaN(parseInt(parts[i], 10))) {
                    const prefixParts: string[] = [];
                    for (let j = 0; j < i; j++) {
                        prefixParts.push(parts[j]);
                    }
                    const prefix = prefixParts.join('_');

                    if (prefix.toLowerCase() === source.templateName.toLowerCase()) {
                        isReuseComponent = true;
                        break;
                    }
                }
            }
        }

        // For reuse components, check if JSON exists
        if (isReuseComponent && sourceJsonCache) {
            // Check if AppSite has JSON cache and the specific template JSON exists
            // sourceJsonCache is a Map<appSiteName, Map<templateName, sourceFile>>
            const appSiteJsonCache = sourceJsonCache.get(appSiteName);
            if (!appSiteJsonCache) {
                continue; // Skip - AppSite not in cache
            }
            // Case-insensitive key lookup (matching C# behavior)
            const lowerPlaceholderName = placeholderName.toLowerCase();
            let hasJsonFile = false;
            for (const key of appSiteJsonCache.keys()) {
                if (key.toLowerCase() === lowerPlaceholderName) {
                    hasJsonFile = true;
                    break;
                }
            }
            if (!hasJsonFile) {
                continue; // Skip - JSON doesn't exist
            }
        }

        // Use getTemplate to retrieve the component with AppView-aware resolution
        // getTemplate will handle component reuse pattern parsing internally
        const componentResult = getTemplate(allTemplates, appSiteName, placeholderName, source.templateName, appView, appViewPrefix);
        const foundComponent = componentResult.template;

        if (foundComponent) {
            const childPathKey = `${foundComponent.templateAppSite ?? ''}/${foundComponent.templateName ?? ''}`;
            if (foundComponent.templateName && ancestorPath.has(childPathKey.toLowerCase())) {
                Logger.warn(`Circular template reference: '${childPathKey}' (referenced via placeholder '${placeholderName}' from '${selfPathKey}') is already in the hierarchy path; skipping to avoid infinite recursion.`, 'GeneratePreProcessJsonMetadata');
                continue;
            }

            const childCopy = copyTemplateWithHierarchy(foundComponent, allTemplates, appSiteName, sourceJsonCache, appView, appViewPrefix, appFileName, ancestorPath, depth + 1, maxRecursionDepth);
            childCopy.placeholderName = placeholderName; // The placeholder name in parent template
            // templateName and templateAppSite are already set from foundComponent

            if (isReuseComponent) {
                childCopy.jsonFileName = placeholderName + '.json';
            }

            copy.components.push(childCopy);
        }
    }

    // Build hierarchy for slotholders
    const allSlotholders = collectAllSlotholders(copy.slotholders);
    for (const slotholder of allSlotholders) {
        const slotName = slotholder.name.replace(/^#/, '');

        // Use getTemplate to retrieve the component with AppView-aware resolution
        const componentResult = getTemplate(allTemplates, appSiteName, slotName, source.templateName, appView, appViewPrefix);
        const foundComponent = componentResult.template;

        if (foundComponent) {
            const childPathKey = `${foundComponent.templateAppSite ?? ''}/${foundComponent.templateName ?? ''}`;
            if (foundComponent.templateName && ancestorPath.has(childPathKey.toLowerCase())) {
                Logger.warn(`Circular template reference: '${childPathKey}' (referenced via slot '${slotName}' from '${selfPathKey}') is already in the hierarchy path; skipping to avoid infinite recursion.`, 'GeneratePreProcessJsonMetadata');
                continue;
            }

            const childCopy = copyTemplateWithHierarchy(foundComponent, allTemplates, appSiteName, sourceJsonCache, appView, appViewPrefix, appFileName, ancestorPath, depth + 1, maxRecursionDepth);
            childCopy.placeholderName = slotName; // The slot name in parent template
            // templateName and templateAppSite are already set from foundComponent

            copy.components.push(childCopy);
        }
    }

    if (addedSelf) {
        ancestorPath.delete(selfPathLower);
    }

    return copy;
}

/**
 * Recursively collects all slotholders from a list, including nested ones
 */
function collectAllSlotholders(slotholders: SlotInfo[]): SlotInfo[] {
    const result: SlotInfo[] = [];

    for (const slotholder of (slotholders || [])) {
        // Add this slotholder
        result.push(slotholder);

        // Recursively collect nested slotholders from slot contents
        for (const slot of (slotholder.slots || [])) {
            if (slot.slotholders && slot.slotholders.length > 0) {
                const nestedSlotholders = collectAllSlotholders(slot.slotholders);
                result.push(...nestedSlotholders);
            }
        }
    }

    return result;
}

/**
 * Deep copies a SlotInfo structure
 */
function copySlotInfo(source: SlotInfo): SlotInfo {
    const copy = new SlotInfo();
    copy.name = source.name;
    copy.slots = [];

    for (const slot of (source.slots || [])) {
        const slotCopy = new SlotContentInfo();
        slotCopy.name = slot.name;
        slotCopy.slotholders = [];

        for (const slotholder of (slot.slotholders || [])) {
            slotCopy.slotholders.push(copySlotInfo(slotholder));
        }

        copy.slots.push(slotCopy);
    }

    return copy;
}
